import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Icon,
  Input,
  Link,
  Select,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useToast,
  Skeleton,
  SkeletonText,
} from "@chakra-ui/react";
import { FiUpload } from "react-icons/fi";
import axiosInstance from "../../../helpers/axiosInstance";
import Upload from "./Upload";
import countryList from "react-select-country-list";

/* new children */
import ManualDataTab from "./ManualDataTab";
import FileUploadWizard from "./FileUploadWizard";
import FileUploadTab from "./FileUploadTab";

const UploadModal = ({
  isOpen,
  onClose,
  selectedProgram,
  fetchSelectedProgram,
  fileUniqueId,
  unsavedManualData,
  setUnsavedManualData,
  contentType,
  campaignType,
}) => {
  /* ---------------- state & derived data ---------------- */
  const [file, setFile] = useState(null);
  const [manualData, setManualData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const toast = useToast();

  const countryOptions = useMemo(() => countryList().getData(), []);
  const [isUploadLoading, setIsUploadLoading] = useState(false);
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [currentClientAnswers, setCurrentClientAnswers] = useState({});

  const columns = useMemo(() => {
    if (contentType === "company") {
      return [
        "prospect_company_name",
        "prospect_company_website_url",
        "searching_news_in_country",
      ];
    } else {
      // personalized
      return [
        "prospect_name",
        "prospect_company_name",
        "prospect_title",
        "personalised_source", // New field
        "prospect_linkedin_about_section",
        "searching_news_in_country",
      ];
    }
  }, [contentType]);

  useEffect(() => {
    // run this effect only when modal is open and IDs are ready
    if (!isOpen || !selectedProgram?._id || !fileUniqueId) return;

    // 1️⃣  if we already have a draft for this file, show it instantly and
    //     skip the network call (no skeleton needed)
    const draft = unsavedManualData[fileUniqueId];
    if (draft) {
      setManualData(draft);
      return;
    }

    // 2️⃣ otherwise fetch fresh rows
    const fetchRows = async () => {
      setIsFetching(true); // <-- skeleton shows only now
      try {
        const { data: fileData = [] } = await axiosInstance.post(
          `/program/get-file-data/${selectedProgram._id}/${fileUniqueId}`
        );

        const mappedData = fileData.data.map((row) => {
          const newRow = {};
          columns.forEach((col) => {
            if (col === "personalised_source") {
              newRow[col] =
                row[col] !== undefined ? row[col] : "LinkedIn About Section";
            } else {
              newRow[col] = row[col] || "";
            }
          });
          if ("searching_news_in_country" in newRow) {
            newRow.searching_news_in_country =
              newRow.searching_news_in_country.toUpperCase();
          }
          if (campaignType === "cross_sell") {
            [
              "current_client_main_contact_full_name",
              "current_client_main_contact_job_title",
              "current_client_main_contact_department",
              "current_client_purchased_proposition",
              "current_client_company_name",
              "current_client_relationship_to_target",
              "current_client_additional_details",
            ].forEach((k) => (newRow[k] = row[k] || ""));
          }
          return newRow;
        });

        setManualData(mappedData);
      } catch (err) {
        toast({
          title: "Load failed",
          description:
            err.response?.data?.message ||
            err.message ||
            "Unable to retrieve file data.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsFetching(false); // <-- skeleton hides here
      }
    };

    fetchRows();
    // ⚠️  don't include unsavedManualData in deps—typing would retrigger the fetch
  }, [isOpen, selectedProgram?._id, fileUniqueId, columns, campaignType]);

  /* --------------- handlers shared by children --------------- */

  // File upload and drag-drop logic
  const handleFileUpload = (e) => setFile(e.target.files[0]);
  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files.length > 0) {
      setFile(e.dataTransfer.files[0]);
    }
  };
  const handleDragOver = (e) => e.preventDefault();

  const handleUploadFile = async () => {
    console.log("in handle upload file", file);
    if (!file) {
      toast({
        title: "No file selected",
        description: "Please select a file to upload.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    if (!selectedProgram) {
      toast({
        title: "No program selected",
        description: "Please select a program before uploading.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Set loading state to true before starting the upload
    setIsUploadLoading(true);

    try {
      await Upload(file, selectedProgram, fileUniqueId, toast);
      setFile(null);
      onClose();
      fetchSelectedProgram(selectedProgram._id);
    } catch (error) {
      console.error("Error uploading file:", error);
      toast({
        title: "Error uploading file",
        description:
          error.message || "An error occurred while uploading the file.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      // Reset the loading state once the upload is done or if an error occurs
      setIsUploadLoading(false);
    }
  };

  const handleUploadFileCustom = async (file, mappingHeaders) => {
    if (!file) {
      toast({
        title: "No file selected",
        description: "Please select a file to upload.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    if (!selectedProgram) {
      toast({
        title: "No program selected",
        description: "Please select a program before uploading.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsUploadLoading(true);

    try {
      const form = new FormData();
      form.append("file", file); // raw file
      form.append("mapping_headers", JSON.stringify(mappingHeaders));
      form.append("current_client", JSON.stringify(currentClientAnswers));

      await axiosInstance.post(
        `/program/upload-file/${selectedProgram._id}/${fileUniqueId}`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      onClose();
      fetchSelectedProgram(selectedProgram._id);
    } catch (err) {
      toast({
        title: "Upload failed",
        description: err.message || "Unable to upload file.",
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setIsUploadLoading(false);
    }
  };
  // Download template logic
  const handleDownloadTemplate = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const content_type =
        campaignType === "cross_sell"
          ? "cross_sell"
          : contentType.replace(/^"(.*)"$/, "$1");

      const response = await axiosInstance.get(
        `/program/download-template/${content_type}`,
        {
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;

      const contentDisposition = response.headers["content-disposition"];
      let filename = "template.csv";

      if (
        contentDisposition &&
        contentDisposition.indexOf("attachment") !== -1
      ) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(contentDisposition);
        if (matches && matches[1]) {
          filename = matches[1].replace(/['"]/g, "");
        }
      }

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      let errorMessage = "An error occurred while uploading the file.";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      console.error("Error uploading file:", error);
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    }
  };

  // Manual data handlers
  const handleInputChange = (rowIndex, field, value) => {
    const updatedData = [...manualData];
    updatedData[rowIndex][field] = value;
    setManualData(updatedData);

    // Save unsaved data
    const newUnsavedData = {
      ...unsavedManualData,
      [fileUniqueId]: updatedData,
    };
    setUnsavedManualData(newUnsavedData);
    localStorage.setItem("unsavedManualData", JSON.stringify(newUnsavedData));
  };

  const handleAddRow = () => {
    const newRow = {};
    columns.forEach((col) => {
      if (col === "personalised_source") {
        newRow[col] = "LinkedIn About Section"; // Set default value here
      } else {
        newRow[col] = "";
      }
    });
    setManualData([...manualData, newRow]);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRows([]);
    } else {
      setSelectedRows(manualData.map((_, index) => index));
    }
    setSelectAll(!selectAll);
  };

  const handleSelectRow = (index) => {
    setSelectedRows((prev) => {
      const newSelectedRows = prev.includes(index)
        ? prev.filter((i) => i !== index)
        : [...prev, index];
      setSelectAll(newSelectedRows.length === manualData.length);
      return newSelectedRows;
    });
  };

  const handleRemoveSelectedRows = () => {
    setManualData((prev) =>
      prev.filter((_, index) => !selectedRows.includes(index))
    );
    setSelectedRows([]);
  };

  const handleSaveManualData = async () => {
    if (manualData.length === 0) {
      toast({
        title: "No data entered",
        description: "Please enter some data before saving.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    if (!selectedProgram) {
      toast({
        title: "No program selected",
        description: "Please select a program before saving.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Convert searching_news_in_country to lowercase if needed
    const dataToSend = manualData.map((row) => ({
      ...row,
      searching_news_in_country:
        row.searching_news_in_country?.toLowerCase?.() || "",
    }));
    setIsSaveLoading(true);

    try {
      const updatedData = await Upload(
        dataToSend,
        selectedProgram,
        fileUniqueId,
        toast,
        true
      );

      if (updatedData) {
        const newUnsavedData = { ...unsavedManualData };
        delete newUnsavedData[fileUniqueId];
        setUnsavedManualData(newUnsavedData);
        localStorage.setItem(
          "unsavedManualData",
          JSON.stringify(newUnsavedData)
        );

        setManualData([]);
        onClose();
        fetchSelectedProgram(selectedProgram._id);
        setIsSaveLoading(false);
      } else {
        setIsSaveLoading(false);
        console.log("Upload failed, data not cleared.");
      }
    } catch (error) {
      setIsSaveLoading(false);
      console.error("Failed to save data:", error);
      toast({
        title: "Save failed",
        description: "Unable to save data. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const getColumnHeader = (col) => {
    // special mapping for cross-sell’s step-1
    if (campaignType === "cross_sell") {
      switch (col) {
        case "prospect_name":
          return "target_first_name";
        case "prospect_company_name":
          return "target_company";
        case "prospect_title":
          return "target_title";
        case "personalised_source":
          return "target_source";
        case "prospect_linkedin_about_section":
          return "target_linkedin_about_section";
        case "searching_news_in_country":
          return "target_country";
        default:
          return col;
      }
    }

    // existing personalised / company logic
    if (col === "searching_news_in_country") return "target_country";
    if (col === "personalised_source") return "Personalised_Source";
    return col;
  };

  /* ---------------- render ---------------- */
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      scrollBehavior="inside"
      overflowY="hidden"
    >
      <ModalOverlay />
      <ModalContent h="80vh" maxH="100vh" maxWidth="91%" overflow="hidden">
        <ModalHeader ml={2}>Upload or Enter Data</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <Tabs>
            <TabList>
              <Tab>Web Form</Tab>
              <Tab>Smart Focus File Upload</Tab>
              <Tab>Custom File Upload</Tab>
            </TabList>

            <TabPanels>
              <TabPanel>
                {isFetching ? (
                  <Box>
                    <Skeleton height="40px" mb={4} />
                    <SkeletonText noOfLines={8} height="40px" spacing="4" />
                  </Box>
                ) : (
                  <ManualDataTab
                    /* data & helpers */
                    columns={columns}
                    manualData={manualData}
                    selectedRows={selectedRows}
                    selectAll={selectAll}
                    countryOptions={countryOptions}
                    isSaveLoading={isSaveLoading}
                    contentType={contentType}
                    campaignType={campaignType}
                    /* handlers */
                    handleSelectAll={handleSelectAll}
                    handleSelectRow={handleSelectRow}
                    handleRemoveSelectedRows={handleRemoveSelectedRows}
                    handleAddRow={handleAddRow}
                    handleInputChange={handleInputChange}
                    handleSaveManualData={handleSaveManualData}
                    getColumnHeader={getColumnHeader}
                  />
                )}
              </TabPanel>
              <TabPanel>
                <FileUploadTab
                  file={file}
                  isUploadLoading={isUploadLoading}
                  /* handlers */
                  handleFileUpload={handleFileUpload}
                  handleDrop={handleDrop}
                  handleDragOver={handleDragOver}
                  handleUploadFile={handleUploadFile}
                  handleDownloadTemplate={handleDownloadTemplate}
                />
              </TabPanel>

              <TabPanel>
                <FileUploadWizard
                  selectedProgramId={selectedProgram._id}
                  fileUniqueId={fileUniqueId}
                  handleDownloadTemplate={handleDownloadTemplate}
                  handleUploadFileCustom={handleUploadFileCustom}
                  handleFileUpload={handleFileUpload}
                  isUploadLoading={isUploadLoading}
                  handleDragOver={handleDragOver}
                  campaignType={campaignType}
                  currentClientAnswers={currentClientAnswers}
                  setCurrentClientAnswers={setCurrentClientAnswers}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default UploadModal;
